/**
 * Settings → Portal Access — Visibility Matrix (Phase 4, Module 4, AD-013).
 *
 * 6-role × 8-section checkbox grid. Auto-saves on change (500 ms debounce).
 * Admin row is always all-true — rendered greyed-out, not editable.
 * internal_chat cells are locked to their hard-gated values (see canSee).
 * Non-admin users see a read-only view.
 *
 * Route: /settings/portal-access (admin-only, gated in App.tsx roles=['admin'])
 * Links into Settings.tsx landing (admin block).
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { Box, Btn3, Card } from '../components/ui';
import {
  APPROVED_DEFAULTS,
  PORTAL_ROLES,
  PORTAL_SECTIONS,
  type PortalRole,
  type PortalSection,
  type VisibilityMatrix,
} from '../lib/visibilityModel';
import {
  getVisibilityMatrix,
  listenVisibilityMatrix,
  saveVisibilityMatrix,
  toggleGlobalCell,
} from '../lib/visibilityStore';

// ─── Labels ───────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<PortalRole, string> = {
  primary_client: 'Primary client',
  co_client: 'Co-client',
  family: 'Family',
  consultant: 'Consultant',
  partner: 'Partner',
  staff: 'Staff',
  admin: 'Admin',
};

const SECTION_LABELS: Record<PortalSection, string> = {
  project_status: 'Project status',
  floor_plan_grid: 'Floor plan / grid',
  concerns_goals: 'Concerns & goals',
  auo_observations: 'AUO observations',
  delivered_report: 'Delivered report',
  payment_details: 'Payment details',
  internal_chat: 'Internal chat',
  project_files: 'Project files',
};

// Sections that cannot be toggled (hard-gated in canSee regardless of matrix).
const HARD_LOCKED_SECTIONS = new Set<PortalSection>(['internal_chat']);

// Roles whose rows cannot be changed (admin is always all-true).
const HARD_LOCKED_ROLES = new Set<PortalRole>(['admin']);



// ─── Main component ───────────────────────────────────────────────────────

export default function SettingsVisibility() {
  const { user, role } = useAuth();
  const { toast } = useToast();
  const isAdmin = role === 'admin';

  const [matrix, setMatrix] = useState<VisibilityMatrix | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Load + listen for live matrix changes.
  useEffect(() => {
    const unsub = listenVisibilityMatrix((m) => {
      setMatrix(m);
      setDirty(false);
    });
    return unsub;
  }, []);

  // Toggle a cell optimistically — single-cell updateDoc (fast path).
  async function handleToggle(r: PortalRole, s: PortalSection, value: boolean) {
    if (!isAdmin) return;
    if (HARD_LOCKED_ROLES.has(r)) return;
    if (HARD_LOCKED_SECTIONS.has(s)) return;
    if (!user?.uid) return;

    // Optimistic local update.
    setMatrix((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [r]: { ...prev[r], [s]: value },
      };
    });
    setDirty(true);

    // Single-cell write (fast path — avoids full matrix overwrite).
    try {
      await toggleGlobalCell(r, s, value, user.uid);
    } catch (e) {
      // If toggleGlobalCell throws (e.g. guard violation), revert optimistic update.
      toast(`Could not update: ${(e as Error).message}`);
      const fresh = await getVisibilityMatrix();
      setMatrix(fresh);
      setDirty(false);
    }
  }

  async function handleResetToDefaults() {
    if (!user?.uid) return;
    const confirm = window.confirm(
      'Reset all visibility settings to the owner-approved defaults? This cannot be undone.',
    );
    if (!confirm) return;
    setSaving(true);
    try {
      await saveVisibilityMatrix(APPROVED_DEFAULTS, user.uid);
      toast('Reset to defaults.', { ok: true });
    } catch (e) {
      toast(`Reset failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  if (!matrix) {
    return (
      <Box title="Portal Access" note="Loading…">
        <p style={{ color: 'var(--ink-mute)', fontSize: 13 }}>Loading visibility matrix…</p>
      </Box>
    );
  }

  // Rows to render: all PORTAL_ROLES except 'admin' is shown last and greyed.
  const rowRoles: PortalRole[] = PORTAL_ROLES.filter((r) => r !== 'admin');
  rowRoles.push('admin');

  const thStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--ink-mute)',
    textAlign: 'center',
    padding: '4px 6px',
    whiteSpace: 'nowrap',
    transform: 'rotate(-45deg)',
    transformOrigin: 'bottom center',
    minWidth: 32,
  };

  const tdStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '4px 8px',
  };

  return (
    <Box
      title="Portal Access"
      note={isAdmin ? 'Admin — controls what each role can see on a project' : 'Read-only (admin access required to change)'}
    >
      {/* Control strip */}
      {isAdmin && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 12, color: 'var(--ink-mute)', flex: 1 }}>
            Changes save automatically. Admin row and Internal chat column are locked.
            {dirty && saving && ' Saving…'}
            {dirty && !saving && ' Unsaved.'}
          </span>
          <Btn3
            intent="ghost"
            size="sm"
            onClick={handleResetToDefaults}
            disabled={saving}
          >
            Reset to defaults
          </Btn3>
        </div>
      )}

      <Card flat style={{ overflowX: 'auto' }}>
        <table
          style={{
            borderCollapse: 'collapse',
            width: '100%',
            fontSize: 12,
          }}
        >
          {/* Column headers */}
          <thead>
            <tr>
              <th
                style={{
                  ...thStyle,
                  transform: 'none',
                  textAlign: 'left',
                  fontSize: 11,
                  color: 'var(--ink-mute)',
                  paddingBottom: 24,
                }}
              >
                Role
              </th>
              {PORTAL_SECTIONS.map((s) => (
                <th key={s} style={{ ...thStyle, paddingBottom: 24 }}>
                  <span style={{ display: 'inline-block' }}>
                    {SECTION_LABELS[s]}
                    {HARD_LOCKED_SECTIONS.has(s) && (
                      <span style={{ marginLeft: 4, fontSize: 9, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 3, padding: '0 3px' }}>locked</span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          {/* Data rows */}
          <tbody>
            {rowRoles.map((r) => {
              const isLockedRow = HARD_LOCKED_ROLES.has(r);
              return (
                <tr
                  key={r}
                  style={{
                    background: isLockedRow ? '#f7f7f7' : undefined,
                    opacity: isLockedRow ? 0.6 : 1,
                  }}
                >
                  {/* Role label cell */}
                  <td
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      padding: '6px 12px 6px 0',
                      whiteSpace: 'nowrap',
                      color: isLockedRow ? 'var(--ink-mute)' : 'var(--ink)',
                    }}
                  >
                    {ROLE_LABELS[r]}
                    {isLockedRow && (
                      <span style={{ marginLeft: 4, fontSize: 9, color: '#667', background: '#f0f4f8', border: '1px solid #dbe4ed', borderRadius: 3, padding: '0 3px' }}>always on</span>
                    )}
                  </td>

                  {/* Section checkboxes */}
                  {PORTAL_SECTIONS.map((s) => {
                    const value = matrix[r]?.[s] ?? false;
                    const isLocked = isLockedRow || HARD_LOCKED_SECTIONS.has(s);
                    const isEditable = isAdmin && !isLocked;

                    return (
                      <td key={s} style={tdStyle}>
                        <input
                          type="checkbox"
                          checked={value}
                          disabled={!isEditable}
                          onChange={(e) => handleToggle(r, s, e.target.checked)}
                          style={{
                            width: 16,
                            height: 16,
                            cursor: isEditable ? 'pointer' : 'not-allowed',
                            opacity: isLocked ? 0.45 : 1,
                          }}
                          title={
                            isLocked
                              ? `${SECTION_LABELS[s]} for ${ROLE_LABELS[r]} is hard-locked`
                              : `${value ? 'Hide' : 'Show'} ${SECTION_LABELS[s]} for ${ROLE_LABELS[r]}`
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Legend */}
      <div
        style={{
          marginTop: 12,
          fontSize: 11,
          color: 'var(--ink-mute)',
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <span>✓ Checked = visible to that role by default</span>
        <span>Locked = hard-gated; cannot be changed here</span>
        <span>Per-project overrides can be set on each Project → Access tab</span>
      </div>
    </Box>
  );
}
